<template>
  <v-layout
    class="discussion-comment full-width"
    column
    align-start
  >
    <v-container class="pa-2">
      <div class="avatar-wrapper">
        <v-layout
          column
          justify-center
          align-center
        >
          <v-avatar
            :color="commentData.color"
            :size="36"
          >
            <span class="white--text">{{commentData.initials}}</span>
          </v-avatar>
          <span class="caption">
            {{comment.displayTime.toHoursMinutesSeconds()}}
          </span>
        </v-layout>
      </div>
      <div class="comment-wrapper">
        <v-layout
          column
          justify-center
          align-start
        >
          <span v-if="comment.deleted" class="font-weight-bold">[Deleted Comment]</span>
          <v-layout
            justify-start
            align-center
          >
            <v-tooltip
              v-if="(
                containsSearchResults
                && comment.parentId
              )"
              top
            >
              <template v-slot:activator="{ on }">
                <v-icon
                  v-on="on"
                  color="grey"
                  :size="14"
                  class="mr-2"
                  :class="{
                    'reply-icon': comment.parentId,
                  }"
                >
                  reply
                </v-icon>
              </template>
              <span class="white--text">Reply</span>
            </v-tooltip>
            <span class="font-weight-bold">{{commentData.fullName}}</span>
            <span class="caption">&nbsp;- {{fromNow(comment.created)}}</span>
          </v-layout>
          <div
            class="full-width"
            v-html="commentData.text"
          />
          <DiscussionActions
            v-if="!containsSearchResults"
            v-bubble.snackbar.error.refresh-discussion-comments.populate-comments.delete-comment
            @toggle-replies="handleToggleReplies"
            v-bind="$props"
            :interaction.sync="localInteraction"
            :is-processing-data.sync="localIsProcessingData"
            :comment="comment"
            :show-replies="showReplies"
          />
        </v-layout>
      </div>
    </v-container>
    <v-layout
      class="discussion-replies pt-2"
      v-if="showReplies"
      column
    >
      <v-container
        v-for="(reply, replyIndex) in comment.replies"
        :key="reply.id"
        :class="{
          'pb-2': replyIndex !== comment.replies.length - 1 || showLoadMoreRepliesButton
        }"
        class="full-width px-0 pt-0"
      >
        <div class="avatar-wrapper">
          <v-layout
            column
            justify-center
            align-center
          >
            <v-avatar
              :color="replyData[replyIndex].color"
              :size="36"
            >
              <span class="white--text">{{replyData[replyIndex].initials}}</span>
            </v-avatar>
            <span class="caption">
              {{reply.displayTime.toHoursMinutesSeconds()}}
            </span>
          </v-layout>
        </div>
        <div class="comment-wrapper">
          <v-layout
            column
            justify-center
            align-start
            class="text-xs-left"
          >
            <div>
              <span class="font-weight-bold">{{replyData[replyIndex].fullName}}</span>
              <span class="caption">&nbsp;- {{fromNow(reply.created)}}</span>
            </div>
            <div v-html="replyData[replyIndex].text"></div>
            <DiscussionActions
              v-bubble.snackbar.error.refresh-discussion-comments.populate-comments.delete-comment
              v-bind="$props"
              :comment="reply"
              :interaction.sync="localInteraction"
              :is-processing-data.sync="localIsProcessingData"
            />
          </v-layout>
        </div>
      </v-container>
      <v-layout
        v-if="(
          discussionRole !== 'anonymous'
          && showLoadMoreRepliesButton
          && comment.replies.length
        )"
        justify-center
      >
        <v-btn
          block
          flat
          @click="loadMoreReplies"
        >
          Load more replies
        </v-btn>
      </v-layout>
    </v-layout>
  </v-layout>
</template>

<script>
import _ from 'lodash';
import moment from 'moment';
import api from '../../../services/api';
import Interaction from '../../../mixins/Interaction';
import DiscussionActions from './DiscussionActions.vue';

export default {
  name: 'DiscussionAttempt',
  components: {
    DiscussionActions,
  },
  mixins: [
    Interaction,
  ],
  props: {
    comment: {
      type: Object,
      required: true,
    },
    containsSearchResults: Boolean,
  },
  data: () => ({
    showReplies: false,
    showLoadMoreRepliesButton: true,
  }),
  computed: {
    commentData() {
      const firstName = _.get(this, 'comment.student.firstName', _.get(this, 'teacher.firstName', 'Unknown'));
      const lastName = _.get(this, 'comment.student.lastName', _.get(this, 'teacher.lastName', 'User'));

      if (this.comment.deleted) {
        return {
          initials: '-',
          fullName: `${firstName} ${lastName}`,
          text: this.comment.text,
          color: 'black',
        };
      }
      return {
        initials: `${firstName.charAt(0).toUpperCase()}${lastName.charAt(0).toUpperCase()}`,
        fullName: `${firstName} ${lastName}`,
        text: this.comment.text,
        color: 'secondary',
      };
    },
    replyData() {
      return this.comment.replies.map((reply) => {
        if (reply.deleted) {
          return {
            initials: '-',
            fullName: '[Deleted]',
            text: '[Comment was deleted]',
            color: 'black',
          };
        }
        const firstName = _.get(reply, 'student.firstName', _.get(this, 'teacher.firstName', 'Unknown'));
        const lastName = _.get(reply, 'student.lastName', _.get(this, 'teacher.lastName', 'User'));
        return {
          initials: `${firstName.charAt(0).toUpperCase()}${lastName.charAt(0).toUpperCase()}`,
          fullName: `${firstName} ${lastName}`,
          text: reply.text,
          color: 'secondary',
        };
      });
    },
  },
  methods: {
    fromNow(time) {
      return moment(time).fromNow();
    },
    getReplies: _.throttle(async function getRepliesThrottled() {
      try {
        if (this.discussionRole === 'anonymous') return this.comment.replies;
        this.localIsProcessingData = true;
        const { data: replies } = await api.getDiscussionComments({
          parentId: this.comment.id,
          target: 'replies',
          pageSize: this.$constants.discussionRepliesPageSize,
          order: [
            [
              'created',
              'ASC',
            ],
            [
              'id',
              'ASC',
            ],
          ],
        });
        this.showLoadMoreRepliesButton = (replies.length === this.$constants.discussionRepliesPageSize);
        return this.populateReplies(replies);
      } catch(e) {
        return this.$emit('error', e);
      } finally {
        this.localIsProcessingData = false;
      }
    }, 5000, {
      leading: true,
    }),
    async loadMoreReplies() {
      try {
        if (this.discussionRole === 'anonymous') return this.comment.replies;
        this.localIsProcessingData = true;
        const { data: replies } = await api.getDiscussionComments({
          parentId: this.comment.id,
          target: 'replies',
          pageSize: this.$constants.discussionRepliesPageSize,
          timestampAfter: this.comment.replies[this.comment.replies.length - 1].created,
          order: [
            [
              'created',
              'ASC',
            ],
            [
              'id',
              'ASC',
            ],
          ],
        });
        this.showLoadMoreRepliesButton = (replies.length === this.$constants.discussionRepliesPageSize);
        return this.populateReplies(replies);
      } catch(e) {
        return this.$emit('error', e);
      } finally {
        this.localIsProcessingData = false;
      }
    },
    populateReplies(replies) {
      this.comment.replies = this.$_(this.comment.replies)
        .concat(replies)
        .uniqBy('id')
        .value();
    },
    async handleToggleReplies(bool) {
      if (bool === this.showReplies) return false;
      if (this.$_.isBoolean(bool)) this.showReplies = bool;
      else this.showReplies = !this.showReplies;
      if (!this.showReplies) return false;
      if (!this.comment.replies.length) await this.getReplies();
      else this.showLoadMoreRepliesButton = true;
      return true;
    },
  },
};
</script>

<style scoped>
  .avatar-wrapper {
    float: left;
  }
  .comment-wrapper {
    margin-left: 44px;
  }
  .discussion-replies {
    padding-left: 55px;
  }
  .reply-spinner-wrapper {
    max-width: 190px;
  }
  .reply-icon {
    position: relative;
    top: -1px;
  }
</style>